// Servizio email via Firebase Cloud Function (server-side SMTP Gmail)
// La Cloud Function gira su server Firebase → nessun problema CORS

const CLOUD_FUNCTION_URL = 'https://us-central1-hop-market-4fb60.cloudfunctions.net/sendEmail';

/**
 * Invia un'email tramite la Cloud Function Firebase.
 */
const sendEmail = async (params) => {
  const response = await fetch(CLOUD_FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to: params.To,
      subject: params.Subject,
      body: params.Body
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error: ${response.status}`);
  }

  return response.json();
};

/**
 * Invia una notifica email al venditore per un nuovo ordine ricevuto.
 */
export const sendOrderNotificationToSeller = async ({
  sellerEmail,
  sellerName,
  orderNumber,
  customerName,
  total,
  items
}) => {
  if (!sellerEmail) {
    console.warn('Email venditore mancante, notifica non inviata');
    return { success: false, error: 'Email venditore mancante' };
  }

  try {
    const itemsList = items.map(item =>
      `- ${item.name} x${item.quantity} — €${(item.price * item.quantity).toFixed(2)}`
    ).join('\n');

    const subject = `Nuovo ordine #${String(orderNumber).padStart(6, '0')} — ${customerName}`;

    const mailBody = `
Ciao ${sellerName},

Hai ricevuto un nuovo ordine!

Numero ordine: #${String(orderNumber).padStart(6, '0')}
Cliente: ${customerName}
Data: ${new Date().toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}

Prodotti ordinati:
${itemsList}

Totale: €${total.toFixed(2)}

Accedi al pannello venditore per gestire l'ordine.

A presto,
Hop
    `.trim();

    const result = await sendEmail({
      To: sellerEmail,
      Subject: subject,
      Body: mailBody
    });

    console.log('Notifica email inviata al venditore:', result);
    return { success: true };
  } catch (error) {
    console.error('Errore nell\'invio della notifica al venditore:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Invia una notifica email al cliente per conferma dell'ordine effettuato.
 */
export const sendOrderNotificationToCustomer = async ({
  customerEmail,
  customerName,
  shopName,
  orderNumber,
  total,
  items
}) => {
  if (!customerEmail) {
    console.warn('Email cliente mancante, notifica non inviata');
    return { success: false, error: 'Email cliente mancante' };
  }

  try {
    const itemsList = items.map(item =>
      `- ${item.name} x${item.quantity} — €${(item.price * item.quantity).toFixed(2)}`
    ).join('\n');

    const subject = `Conferma ordine #${String(orderNumber).padStart(6, '0')} — ${shopName}`;

    const mailBody = `
Ciao ${customerName},

Grazie per il tuo ordine!

Negozio: ${shopName}
Numero ordine: #${String(orderNumber).padStart(6, '0')}
Data: ${new Date().toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}

Prodotti ordinati:
${itemsList}

Totale: €${total.toFixed(2)}

Il venditore sta preparando il tuo ordine. Riceverai un aggiornamento quando sarà confermato.

A presto,
Hop
    `.trim();

    const result = await sendEmail({
      To: customerEmail,
      Subject: subject,
      Body: mailBody
    });

    console.log('Notifica email inviata al cliente:', result);
    return { success: true };
  } catch (error) {
    console.error('Errore nell\'invio della notifica al cliente:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Invia un'email al cliente quando il venditore conferma l'ordine,
 * con la lista aggiornata dei prodotti (disponibili e non).
 */
export const sendOrderConfirmationToCustomer = async ({
  customerEmail,
  customerName,
  shopName,
  orderNumber,
  total,
  items,
  shippingCost
}) => {
  if (!customerEmail) {
    console.warn('Email cliente mancante, notifica di conferma non inviata');
    return { success: false, error: 'Email cliente mancante' };
  }

  try {
    const availableItems = items.filter(item => item.isAvailable !== false);
    const unavailableItems = items.filter(item => item.isAvailable === false);

    const availableList = availableItems.length > 0
      ? availableItems.map(item =>
          `- ${item.name} x${item.quantity} — €${(item.price * item.quantity).toFixed(2)}`
        ).join('\n')
      : '';

    const unavailableList = unavailableItems.length > 0
      ? unavailableItems.map(item =>
          `- ${item.name} x${item.quantity} (non disponibile)`
        ).join('\n')
      : '';

    const subject = `Ordine #${String(orderNumber).padStart(6, '0')} confermato — ${shopName}`;

    const mailBody = `
Ciao ${customerName},

Il tuo ordine è stato confermato da ${shopName}!

Numero ordine: #${String(orderNumber).padStart(6, '0')}
Data: ${new Date().toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}

${availableList ? `Prodotti confermati:
${availableList}` : ''}

${unavailableList ? `
Prodotti non disponibili:
${unavailableList}` : ''}

Subtotale: €${items.reduce((sum, i) => sum + (i.isAvailable !== false ? i.price * i.quantity : 0), 0).toFixed(2)}
Spedizione: €${(shippingCost || 0).toFixed(2)}
Totale: €${total.toFixed(2)}

${unavailableItems.length > 0 ? 'Nota: alcuni prodotti non sono disponibili e sono stati esclusi dal totale.' : ''}

Puoi procedere al pagamento accedendo al tuo carrello.

A presto,
Hop
    `.trim();

    const result = await sendEmail({
      To: customerEmail,
      Subject: subject,
      Body: mailBody
    });

    console.log('Email di conferma ordine inviata al cliente:', result);
    return { success: true };
  } catch (error) {
    console.error('Errore nell\'invio della conferma ordine al cliente:', error);
    return { success: false, error: error.message };
  }
};
